import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ListColumnType } from 'types/ListTypes';
import AbstractList from 'components/lists/AbstractList';
import { renderDate, renderPriceDecimalColumn } from 'components/lists/functions/renderListColumn';
import { useSettings } from 'services/settings/SettingsContext';
import '../../style/scss/billing.scss';

// The statuses of a planning row which are not invoiced yet
const OPEN_STATUSES = ['expected', 'pending', 'no_invoice'];

const BillingPlanningList: React.FC = () => {
    const { t } = useTranslation();
    const { userLocale } = useSettings();

    // Keep track of the shown year, starting with the current year
    const currentYear = new Date().getFullYear();
    const [year, setYear] = useState<number>(currentYear);

    // Specify the options of this list, the year is part of the endpoint so a switch remounts the list
    const query = {
        endpoint: `get_billing_planning_as_superuser/${year}`,
        object: 'billing_planning'
    }
    const objectName = 'billing_planning.general.object_name'

    // Format the amount based on the user locale
    const formatAmount = (amount: string | number) => {
        return new Intl.NumberFormat(userLocale, { style: 'currency', currency: 'EUR' }).format(Number(amount));
    };

    // Format the month group header with the month name and the total of the month
    const formatMonthGroup = (groupValue: string) => {
        const [monthKey, total] = groupValue.split('|');
        const [yearPart, monthPart] = monthKey.split('-');
        const monthDate = new Date(Number(yearPart), Number(monthPart) - 1, 1);
        const monthName = monthDate.toLocaleDateString(userLocale, { month: 'long', year: 'numeric' });
        const capitalizedMonth = monthName.charAt(0).toUpperCase() + monthName.slice(1);
        return `${capitalizedMonth} · ${formatAmount(total)}`;
    };

    // Render the status of a planning row with the invoice number behind it
    const renderStatus = (row: Record<string, any>) => {
        return (
            <div className={`billing-planning-status ${OPEN_STATUSES.includes(row.status) ? 'open' : row.status === 'paid' || row.status === 'paid_externally' ? 'paid' : 'invoiced'}`}>
                {t(`billing_planning.status.${row.status}`)}{row.invoice_number ? ` (${row.invoice_number})` : ''}
            </div>
        );
    };

    // Specify the columns of this list:
    const columns: ListColumnType[] = [
        { 
            width: '0.75fr', 
            field: 'date', 
            label: 'billing_planning.general.date_label',
            render: (_, row) => renderDate(row.date, userLocale),
            disableOrdering: true 
        },
        { 
            width: '2fr', 
            field: 'environment_name', 
            label: 'billing_planning.general.customer_label',
            disableOrdering: true 
        },
        { 
            width: '0.9fr', 
            field: 'amount', 
            label: 'billing_planning.general.amount_label',
            render: (_, row) => renderPriceDecimalColumn(row.amount, 'EUR', userLocale),
            disableOrdering: true 
        },
        { 
            width: '1fr', 
            field: 'status', 
            label: 'subscription.general.status_label',
            render: (_, row) => renderStatus(row),
            disableOrdering: true 
        },
    ];

    return (
        <AbstractList
            key={year}
            query={query}
            objectName={objectName}
            columns={columns}
            isPaginated={false}
            showAddButton={false}
            groupByField={{ field: 'month_group', formatValue: formatMonthGroup }}
            changeViewButton={{
                buttons: [
                    { label: String(currentYear + 1), tabValue: String(currentYear + 1) },
                    { label: String(currentYear), tabValue: String(currentYear) },
                    { label: String(currentYear - 1), tabValue: String(currentYear - 1) },
                ],
                currentView: String(year),
                onViewChange: (view) => setYear(Number(view)),
            }}
        />
    );
};

export default BillingPlanningList;
